import { useEffect, useMemo, useState } from "react";
import { getQuoteExtras, requestExtras } from "../../api/quotePage";
import { formatMoney } from "../../utils/money";
import SilentBoundary from "../common/SilentBoundary";
import GoogleReviews from "./GoogleReviews";

// Everything below the quote on the pay page: extras the customer can ask for,
// Google reviews, and the FAQ. Loaded on its own after the quote; if any of it
// fails, it simply doesn't show. Choosing extras only sends a *request* to
// Birdie Bus: the quote, its total and its pay buttons stay exactly as they are
// until an updated quote is sent.

export default function QuoteExtras({ token, quote }) {
  const [extras, setExtras] = useState(null);

  useEffect(() => {
    let active = true;
    getQuoteExtras(token)
      .then((x) => active && setExtras(x))
      .catch(() => active && setExtras(null));
    return () => {
      active = false;
    };
  }, [token]);

  if (!extras) return null;
  const showExtras = extras.can_request || extras.open_request;

  return (
    <div className="space-y-4 mt-4">
      {showExtras && (
        <SilentBoundary token={token} where="/pay (extras)">
          <ExtrasCard
            token={token}
            quote={quote}
            extras={extras}
            onRequested={(req) => setExtras((x) => ({ ...x, open_request: req }))}
          />
        </SilentBoundary>
      )}
      {extras.google_place_id && (
        <SilentBoundary token={token} where="/pay (reviews)">
          <GoogleReviews placeId={extras.google_place_id} />
        </SilentBoundary>
      )}
      {extras.faq?.length > 0 && (
        <SilentBoundary token={token} where="/pay (faq)">
          <Faq items={extras.faq} />
        </SilentBoundary>
      )}
    </div>
  );
}

function ExtrasCard({ token, quote, extras, onRequested }) {
  const open = extras.open_request;
  const [editing, setEditing] = useState(!open);
  const [picked, setPicked] = useState(() => new Set());
  const [upgrade, setUpgrade] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const rate = Number(extras.tax_rate_basis_points || 0);
  const addedCents = useMemo(() => {
    let sum = 0;
    for (const a of extras.addons) if (picked.has(a.id)) sum += a.price_cents;
    if (upgrade && extras.upgrade) sum += extras.upgrade.price_diff_cents;
    return sum;
  }, [picked, upgrade, extras]);
  // Extras are taxed like the rest of the event; discounts and credits are
  // fixed amounts, so the total simply grows by the extras plus their tax.
  const newTotal = Number(quote.total_cents || 0) + Math.round((addedCents * (10000 + rate)) / 10000);
  const paidSomething = Number(quote.amount_paid_cents || 0) > 0;

  const toggle = (id) =>
    setPicked((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  async function send() {
    setSending(true);
    setError("");
    try {
      const res = await requestExtras(token, { addonIds: [...picked], upgrade, note: note.trim() });
      onRequested(res.request);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Couldn't send that. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-birdie-green">Make it even better</h2>
          <p className="text-sm text-birdie-charcoal/65 mt-0.5">
            Add extras to your event. We'll confirm and send you an updated quote.
          </p>
        </div>
        <img
          src="/mascot.png"
          alt=""
          className="h-16 w-auto shrink-0 -mt-1"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>

      {open && !editing ? (
        <div className="mt-4 rounded-xl bg-birdie-green/10 border border-birdie-green/20 p-3 text-sm">
          <div className="font-semibold text-birdie-green">Request sent ✓</div>
          <div className="text-birdie-charcoal/80 mt-1">
            {open.items.map((i) => i.name).join(", ")}{" "}
            <span className="text-birdie-charcoal/55">(about +{formatMoney(open.est_cents)} before tax)</span>
          </div>
          <div className="text-xs text-birdie-charcoal/60 mt-2">
            We'll send your updated quote shortly.{" "}
            {paidSomething
              ? "The extras will be added to your remaining balance."
              : "You can still pay your deposit now; extras are added to your balance."}
          </div>
          {extras.can_request && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-2 text-xs font-semibold text-birdie-green underline"
            >
              Change my request
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {extras.upgrade && (
              <Choice
                checked={upgrade}
                onChange={() => setUpgrade((v) => !v)}
                title={`Upgrade to ${extras.upgrade.name}`}
                detail={`${extras.upgrade.hours} hours instead of your current package`}
                price={`+${formatMoney(extras.upgrade.price_diff_cents)}`}
                highlight
              />
            )}
            {extras.addons.map((a) => (
              <Choice
                key={a.id}
                checked={picked.has(a.id)}
                onChange={() => toggle(a.id)}
                title={a.name}
                detail={a.description}
                price={`+${formatMoney(a.price_cents)}`}
              />
            ))}
          </div>

          {addedCents > 0 && (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="Anything we should know? (e.g. what the sign should say)"
                className="mt-3 w-full px-3 py-2.5 text-sm border border-birdie-green/25 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-birdie-green/40"
              />
              <div className="mt-3 flex justify-between text-sm">
                <span className="text-birdie-charcoal/70">New estimated total</span>
                <span className="font-semibold text-birdie-charcoal">{formatMoney(newTotal)}</span>
              </div>
              <p className="text-[11px] text-birdie-charcoal/50 mt-0.5">
                Includes estimated tax. Your current quote and pay buttons above don't change until we send the
                update.
              </p>
            </>
          )}

          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}

          <button
            type="button"
            onClick={send}
            disabled={sending || addedCents === 0}
            className="mt-4 block w-full text-center font-bold rounded-xl px-6 py-3.5 transition bg-birdie-mustard text-birdie-charcoal disabled:opacity-40"
          >
            {sending ? "Sending…" : addedCents === 0 ? "Pick an extra above" : "Request these extras"}
          </button>
        </>
      )}
    </Card>
  );
}

function Choice({ checked, onChange, title, detail, price, highlight }) {
  return (
    <label
      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${
        checked
          ? "border-birdie-green bg-birdie-green/5"
          : highlight
          ? "border-birdie-mustard/60 bg-birdie-mustard/5"
          : "border-birdie-green/15 bg-white"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-5 w-5 rounded border-birdie-green/40 text-birdie-green focus:ring-birdie-green/40"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-birdie-charcoal">{title}</span>
        {detail && <span className="block text-xs text-birdie-charcoal/60 mt-0.5">{detail}</span>}
      </span>
      <span className="shrink-0 text-sm font-semibold text-birdie-charcoal tabular-nums">{price}</span>
    </label>
  );
}

function Faq({ items }) {
  return (
    <Card>
      <h2 className="font-heading text-xl text-birdie-green mb-2">Good to know</h2>
      <div className="divide-y divide-birdie-green/10">
        {items.map((item, i) => (
          <details key={i} className="group py-3">
            <summary className="flex justify-between items-center gap-3 cursor-pointer list-none text-sm font-semibold text-birdie-charcoal">
              {item.q}
              <span className="shrink-0 text-birdie-green transition group-open:rotate-45 text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-birdie-charcoal/70 whitespace-pre-line">{item.a}</p>
          </details>
        ))}
      </div>
    </Card>
  );
}

function Card({ children }) {
  return <div className="bg-white border border-birdie-green/15 rounded-2xl p-5 sm:p-6 shadow-sm">{children}</div>;
}
